import InvalidArgumentError from "../../Errors/InvalidArgumentError";
import { EthnicityBmi } from "@sisuwellness/utilities/UserJourney/UserJourneyMap";

/**
 * BMI (Body Mass Index) Guideline implementation following NICE guidelines 1.9.10 and 1.9.11
 *
 * This class implements BMI categorization with ethnicity-specific adjustments according to
 * NICE (National Institute for Health and Care Excellence) guidelines for obesity assessment.
 *
 * Categories mapped from NICE guidelines:
 * - Low (<18.5): Underweight
 * - Normal (18.5-24.9): Healthy weight
 * - Elevated (25-29.9): Overweight
 * - High (30-34.9): Obesity class 1
 * - Very High (35+): Obesity class 2 & 3 combined
 *
 * For populations defined in EthnicityBmi (Bangladeshi, Indian, Pakistani, Chinese,
 * Another Asian background, Other Asian, Arab, African, Black African, Caribbean,
 * Black Caribbean, Another Black background), thresholds are reduced by 2.5 kg/m² for obesity classes
 * as these populations have higher cardiometabolic risk at lower BMI values.
 */
export default class SiSU {
    /**
     * Constructor
     * @param {number} bmi - The BMI value
     * @param {string|null} ethnicity - Optional ethnicity string or question code (e.g., 'Caribbean', 'UK_ETHNICITY_ASIAN')
     */
    constructor(bmi, ethnicity = null) {
        this._validateBMIValue(bmi);
        this._bmi = bmi;
        // NHS reports BMI to one decimal place. We classify against the 1dp value (not the raw
        // 6dp value) so the band shown to the user always matches the number shown to the user.
        // e.g. raw 24.94 -> displays 24.9 -> Normal; raw 24.96 -> displays 25.0 -> Elevated.
        this._displayBmi = SiSU.roundToOneDecimal(bmi);
        this._ethnicity = ethnicity;
        this._adjustedThresholds = this._getAdjustedThresholds();
    }

    /**
     * Rounds a BMI value to one decimal place using standard half-up rounding,
     * matching NHS BMI reporting (e.g. 24.2 rather than 24).
     *
     * @param {number} value
     * @returns {number}
     */
    static roundToOneDecimal(value) {
        return Math.round((value + Number.EPSILON) * 10) / 10;
    }

    /**
     * Returns the raw (unrounded) BMI value as supplied. Preserves full precision
     * for storage/data fidelity — use {@link displayValue} for what the user sees.
     * @returns {Number}
     */
    get() {
        return this._bmi;
    }

    /**
     * Returns the BMI rounded to one decimal place (NHS reporting). This is the
     * value that should be displayed AND the value classification is based on.
     * @returns {Number}
     */
    get displayValue() {
        return this._displayBmi;
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "BODY_MASS_INDEX";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "SISU_BMI";
    }

    /**
     * Validates the given BMI value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateBMIValue(bmi) {
        if (!(typeof bmi === "number") || Number.isNaN(bmi) || bmi < 0) {
            throw new InvalidArgumentError("BMI must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Whether the given ethnicity value/code requires NICE 1.9.11 adjusted thresholds.
     * Accepts a resolved NHS ethnicity value (e.g. "Caribbean") or a fallback question
     * code — both forms are members of EthnicityBmi.
     * @param {string|null} ethnicity
     * @returns {boolean}
     */
    static isEthnicityAdjusted(ethnicity) {
        return !!ethnicity && EthnicityBmi.includes(ethnicity);
    }

    /**
     * Category-band UPPER boundaries for the given ethnicity. Single source of truth for
     * both this guideline and the Weight guideline (which converts these BMI thresholds to
     * weights for a given height), so the two never drift apart.
     *
     * For EthnicityBmi populations (NICE 1.9.11): overweight 25→23, obesity 30→27.5,
     * obesity class 2 35→32.5. Otherwise standard NICE 1.9.10 thresholds.
     *
     * @param {string|null} ethnicity
     * @returns {{low:number, normal:number, elevated:number, high:number, veryHigh:number}}
     */
    static thresholdsForEthnicity(ethnicity) {
        return SiSU.isEthnicityAdjusted(ethnicity)
            ? { low: 18.5, normal: 23, elevated: 27.5, high: 32.5, veryHigh: 32.5 }
            : { low: 18.5, normal: 25, elevated: 30, high: 35, veryHigh: 35 };
    }

    /**
     * The smallest increment we display. The normal band's upper bound is reported as
     * one step below the overweight threshold — the highest value we can show that still
     * sits inside the band (standard 25 -> 24.9, adjusted 23 -> 22.9).
     * @returns {number}
     */
    static get _DISPLAY_STEP() {
        return 0.1;
    }

    /**
     * The healthy BMI band for the given ethnicity, per NICE 1.9.10 / 1.9.11. Reporting the
     * full band rather than a single target is what lets underweight users see what to aim
     * up to, and stops adjusted-threshold users being given a target inside their own
     * elevated band.
     *
     * @param {string|null} ethnicity
     * @returns {{lower: number, upper: number}}
     */
    static healthyBmiRangeForEthnicity(ethnicity) {
        const thresholds = SiSU.thresholdsForEthnicity(ethnicity);
        return {
            lower: thresholds.low,
            upper: Math.round((thresholds.normal - SiSU._DISPLAY_STEP) * 10) / 10
        };
    }

    /**
     * Get adjusted BMI thresholds based on ethnicity according to NICE guidelines.
     * Delegates to the shared static so the Weight guideline uses identical cutoffs.
     *
     * @returns {object} Threshold values for BMI categorization (UPPER boundary of each band)
     * @private
     */
    _getAdjustedThresholds() {
        return SiSU.thresholdsForEthnicity(this._ethnicity);
    }

    /**
     * Determine if ethnicity requires BMI threshold adjustment.
     * Checks if the ethnicity value is included in the EthnicityBmi array,
     * which contains ethnicity strings and question codes requiring adjusted thresholds per NICE guidelines 1.9.11.
     *
     * @returns {string} 'adjusted' if ethnicity is in EthnicityBmi, 'standard' otherwise
     * @private
     */
    _getEthnicityCategory() {
        return SiSU.isEthnicityAdjusted(this._ethnicity) ? "adjusted" : "standard";
    }

    /**
     * Get display ranges adjusted for ethnicity.
     * Returns the appropriate BMI ranges for display based on the person's ethnicity.
     *
     * @returns {object} Object containing range information for each BMI category
     */
    /**
     * Instance-level band ranges, ethnicity-aware. Alias of {@link adjustedRanges}.
     *
     * This exists because consumers reach for `guideline.ranges` — either directly, or via the
     * `guideline.ranges || guideline.constructor.ranges` idiom that every other guideline
     * satisfies with an instance getter. Without one, BMI silently fell through to the STATIC
     * `ranges`, whose band labels are the un-adjusted NICE 1.9.10 literals. An adjusted-threshold
     * member would then see "18.5 - 24.9 Normal" printed beside a point rated on 18.5 - 22.9.
     *
     * Keep this in step with `adjustedRanges` by delegating, never by duplicating the literals.
     *
     * @returns {object}
     */
    get ranges() {
        return this.adjustedRanges;
    }

    get adjustedRanges() {
        const ethnicityCategory = this._getEthnicityCategory();

        // Return adjusted ranges
        if (ethnicityCategory === "adjusted") {
            return {
                low: {
                    key: "low",
                    label: "Low",
                    range: "< 18.5"
                },
                normal: {
                    key: "normal",
                    label: "Normal",
                    range: "18.5 - 22.9"
                },
                elevated: {
                    key: "elevated",
                    label: "Elevated",
                    range: "23 - 27.4"
                },
                high: {
                    key: "high",
                    label: "High",
                    range: "27.5 - 32.4"
                },
                veryHigh: {
                    key: "veryHigh",
                    label: "Very High",
                    range: "32.5+"
                }
            };
        }

        // Standard ranges for populations not in EthnicityBmi (NICE 1.9.10)
        return SiSU.ranges;
    }

    /**
     * Returns the ethnicity category for debugging/logging
     * @returns {string}
     */
    get ethnicityCategory() {
        return this._getEthnicityCategory();
    }

    /**
     * Returns an object representing all labels corresponding to this guideline defination
     *
     * @returns {object}
     */
    static get labels() {
        return {
            low: "Low",
            normal: "Normal",
            elevated: "Elevated",
            high: "High",
            veryHigh: "Very High"
        };
    }

    /**
     * Returns an object representing various data for each BMI range
     *
     * @returns {object}
     */
    static get ranges() {
        return {
            low: {
                key: "low",
                label: "Low",
                range: "< 18.5"
            },

            normal: {
                key: "normal",
                label: "Normal",
                range: "18.5 - 24.9"
            },

            elevated: {
                key: "elevated",
                label: "Elevated",
                range: "25 - 29.9"
            },

            high: {
                key: "high",
                label: "High",
                range: "30 - 34.9"
            },

            veryHigh: {
                key: "veryHigh",
                label: "Very High",
                range: "35+"
            }
        };
    }

    /**
     * Returns if the given BMI is in the 'low' range (underweight)
     * BMI < 18.5 kg/m² (same for all ethnicities)
     *
     * @returns {boolean}
     */
    isLow() {
        return this._displayBmi < this._adjustedThresholds.low;
    }

    /**
     * Returns if the given BMI is in the 'normal' range (healthy weight)
     * Standard: 18.5-24.9 kg/m²
     * Adjusted: 18.5-22.9 kg/m² (EthnicityBmi populations)
     *
     * @returns {boolean}
     */
    isNormal() {
        return this._displayBmi >= this._adjustedThresholds.low && this._displayBmi < this._adjustedThresholds.normal;
    }

    /**
     * Returns if the given BMI is in the 'elevated' range (overweight)
     * Standard: 25-29.9 kg/m²
     * Adjusted: 23-27.4 kg/m² (EthnicityBmi populations)
     *
     * @returns {boolean}
     */
    isElevated() {
        return (
            this._displayBmi >= this._adjustedThresholds.normal && this._displayBmi < this._adjustedThresholds.elevated
        );
    }

    /**
     * Returns if the given BMI is in the 'high' range (obesity class 1 & 2)
     * Standard: 30-34.9 kg/m²
     * Adjusted: 27.5-32.4 kg/m² (EthnicityBmi populations)
     *
     * @returns {boolean}
     */
    isHigh() {
        return (
            this._displayBmi >= this._adjustedThresholds.elevated && this._displayBmi < this._adjustedThresholds.high
        );
    }

    /**
     * Returns if the given BMI is in the 'very high' range (obesity class 3)
     * Standard: ≥35 kg/m²
     * Adjusted: ≥32.5 kg/m² (EthnicityBmi populations)
     *
     * @returns {boolean}
     */
    isVeryHigh() {
        return this._displayBmi >= this._adjustedThresholds.veryHigh;
    }

    /**
     * returns help information about the calculated range
     *
     * @returns {string}
     */
    get rangeInfo() {
        let info = "";
        const ranges = this.adjustedRanges;
        switch (true) {
            case this.isLow():
                info = ranges.low.range;
                break;
            case this.isNormal():
                info = ranges.normal.range;
                break;
            case this.isElevated():
                info = ranges.elevated.range;
                break;
            case this.isHigh():
                info = ranges.high.range;
                break;
            case this.isVeryHigh():
                info = ranges.veryHigh.range;
                break;
        }

        return info;
    }

    /**
     * returns key about the calculated range
     *
     * @returns {string}
     */
    get key() {
        let info = "";
        const ranges = this.adjustedRanges;
        switch (true) {
            case this.isLow():
                info = ranges.low.key;
                break;
            case this.isNormal():
                info = ranges.normal.key;
                break;
            case this.isElevated():
                info = ranges.elevated.key;
                break;
            case this.isHigh():
                info = ranges.high.key;
                break;
            case this.isVeryHigh():
                info = ranges.veryHigh.key;
                break;
        }

        return info;
    }

    /**
     * Returns label for the calculated range
     *
     * @returns {string}
     */
    get label() {
        let info = "";
        const ranges = this.adjustedRanges;
        switch (true) {
            case this.isLow():
                info = ranges.low.label;
                break;
            case this.isNormal():
                info = ranges.normal.label;
                break;
            case this.isElevated():
                info = ranges.elevated.label;
                break;
            case this.isHigh():
                info = ranges.high.label;
                break;
            case this.isVeryHigh():
                info = ranges.veryHigh.label;
                break;
        }

        return info;
    }

    /**
     * Calculates a risk level number between 0 - 1 for each rating
     *
     * @return {number}
     */
    get riskLevel() {
        let totalRiskLevel = 5,
            riskLevel = 0;
        switch (true) {
            case this.isLow():
                riskLevel = 1 / (totalRiskLevel + 1);
                break;
            case this.isNormal():
                riskLevel = 2 / (totalRiskLevel + 1);
                break;
            case this.isElevated():
                riskLevel = 3 / (totalRiskLevel + 1);
                break;
            case this.isHigh():
                riskLevel = 4 / (totalRiskLevel + 1);
                break;
            case this.isVeryHigh():
                riskLevel = 5 / (totalRiskLevel + 1);
                break;
        }

        return riskLevel;
    }
}
